import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { 
  ChevronDown, 
  ChevronRight, 
  Search, 
  HelpCircle, 
  BookOpen, 
  Users, 
  CreditCard, 
  Settings,
  Zap,
  Shield,
  MessageSquare,
  Star,
  TrendingUp
} from 'lucide-react';

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedItems, setExpandedItems] = useState(new Set());

  const categories = [
    { id: 'all', label: 'All Categories', icon: HelpCircle },
    { id: 'getting-started', label: 'Getting Started', icon: BookOpen },
    { id: 'training', label: 'Training & Education', icon: BookOpen },
    { id: 'lead-capture', label: 'Lead Capture', icon: Zap },
    { id: 'pif-system', label: 'PIF System', icon: Users },
    { id: 'billing', label: 'Billing & Credits', icon: CreditCard },
    { id: 'technical', label: 'Technical Support', icon: Settings },
    { id: 'account', label: 'Account Management', icon: Shield }
  ];

  const faqData = [
    // Getting Started
    {
      id: 1,
      category: 'getting-started',
      question: 'What is BBC Quick List and how does it work?',
      answer: `BBC Quick List is a comprehensive all-in-one marketing platform designed to help entrepreneurs build their email lists and grow their businesses. The platform provides everything you need in one place:

**Core Features:**
• **Boot Camp Training (FREE)**: 3 comprehensive eBooks (155+ pages total) covering list building, lead magnets, and social media conversion
• **Lead Capture System**: Professional templates for various industries (insurance, legal services, MLM, general opportunities)
• **Rookie Onboarding Wizard**: Step-by-step 10-minute setup to get you started fast
• **QR Code Generator**: Turn offline traffic into online leads
• **Marketing Library**: Done-for-you content, angles, and templates
• **Training Vault**: Progressive education with quizzes and achievement badges
• **Gamification System**: Earn XP, level up, and unlock achievements as you progress
• **Founders Program**: Exclusive benefits and lifetime rewards for early members
• **Compensation Engine**: Member-to-member 1x2 and 12x3 matrix systems with auto-repurchase

**Membership Tiers:**
Start with **FREE Boot Camp access** (complete training, tools, and community), then upgrade to Rookie Pro ($47/mo), Pro ($97/mo), or Enterprise ($197/mo) as your business grows.

**How It Works:**
1. Sign up for free Boot Camp access
2. Complete the Rookie onboarding (10 minutes)
3. Access training vault and build your first lead capture page
4. Generate your QR code and start capturing leads
5. All leads go directly to your dashboard - download as CSV anytime
6. Progress through levels, earn badges, and unlock features

No complex setup. No hidden fees. Start building your list today!`,
      tags: ['basics', 'overview', 'platform', 'features']
    },
    {
      id: 2,
      category: 'getting-started',
      question: 'How do I get started after signing up?',
      answer: `Getting started is simple with our **Rookie Onboarding Wizard**:

**Automatic Onboarding (10-Step Process):**
1. **Welcome** - Introduction to BBC Quick List
2. **Set Your Basics** - Add your name, timezone, contact info
3. **Add Wallet** - Connect your Tron (USDT-TRC20) wallet for payouts
4. **Upload Avatar** - Add a professional photo (builds trust)
5. **View Rookie Cheat Sheet** - Access all free tools and resources
6. **Publish Your First Page** - Choose from pre-made templates
7. **Create QR Code** - Generate your first trackable QR code
8. **Share Once** - Send your link to one person (6 proven message templates provided)
9. **Earn Your Badge** - Claim your Rookie Initiate badge (+200 XP)
10. **Enter Boot Camp** - Start List Building 101 training

**After Onboarding:**
• Access all 3 Boot Camp eBooks (155+ pages)
• Browse the Marketing Library for content
• Create more lead capture pages
• Generate QR codes for offline marketing
• Start earning XP and leveling up
• Join the community and connect with other members

**All leads captured go to your Back Office** - download as CSV anytime. No need for external autoresponders until you reach Enterprise level!

**Pro Tip:** Complete onboarding in under 10 minutes to earn the "Speed Demon" achievement badge! ⚡`,
      tags: ['onboarding', 'setup', 'first-steps', 'wizard', 'rookie']
    },

    // Training & Education
    {
      id: 3,
      category: 'training',
      question: 'What training materials are included in Boot Camp?',
      answer: `Boot Camp includes 3 comprehensive eBooks:

**1. List Building for Real People (45 pages)**
• 14-day action plan to build your first 100-500 subscribers
• Blue-collar friendly examples and strategies
• Mobile-first approach for busy workers
• Real scripts and templates

**2. Magnets That Work (52 pages)**
• 21 proven lead magnet templates
• Industry-specific examples
• Quick setup guides (build tonight!)
• Conversion optimization tips

**3. From Followers to Owned Audience (58 pages)**
• Convert social media followers to email subscribers
• Platform-specific strategies (TikTok, YouTube, Instagram, LinkedIn)
• Ethical automation techniques
• 30-day audience transfer plan

Plus achievement badges, progress tracking, and quiz assessments!`,
      tags: ['training', 'ebooks', 'content', 'boot-camp']
    },
    {
      id: 4,
      category: 'training',
      question: 'How do the achievement badges work?',
      answer: `Our achievement system rewards your progress:

**Chapter Milestones**: Earn badges for completing chapters (Promise Creator, Format Picker, Action Taker)

**eBook Completions**: Major achievements for finishing each book:
• List Building Master
• Magnet Creator  
• Audience Builder

**Series Graduation**: Complete all 3 eBooks to become a "BBC Rookie Graduate" and unlock Rookie Pro access

**Ranking Posters**: Auto-generated achievement posters with your photo and accomplishment that you can share on social media

Badges appear on your profile and unlock new training levels!`,
      tags: ['achievements', 'progress', 'gamification', 'badges']
    },

    // Lead Capture
    {
      id: 5,
      category: 'lead-capture',
      question: 'How do I create my first lead capture page?',
      answer: `Creating lead capture pages is simple:

**Step 1: Choose a Template**
• Visit Lead Capture Marketplace
• Browse templates by industry
• Preview and select based on your membership level

**Step 2: Customize Your Page**
• Add your headline and offer
• Upload your lead magnet (PDF, checklist, etc.)
• Customize colors and branding

**Step 3: Publish & Promote**
• Get your unique page URL
• Share on social media, QR codes, or direct messages
• Track leads in your dashboard

**Templates Available by Membership:**
• Boot Camp: 9+ industry-specific templates
• Rookie Pro+: Advanced templates with A/B testing`,
      tags: ['lead-capture', 'templates', 'setup', 'marketplace']
    },
    {
      id: 6,
      category: 'lead-capture',
      question: 'What types of lead magnets work best?',
      answer: `The most effective lead magnets are:

**Checklists & Cheat Sheets** (Fastest to create)
• "5-Step Safety Checklist for Construction Workers"
• "10-Point Restaurant Opening Checklist"

**Mini-Quizzes** (High engagement)
• "Which Side Hustler Type Are You?"
• "What's Your Marketing Personality?"

**5-Minute Audits** (Build authority)
• "Rate Your Social Media Strategy"
• "Assess Your Business Readiness"

**Industry-Specific Examples:**
• Mechanics: Car diagnostic guides
• Nurses: Shift survival kits  
• Restaurants: Food cost calculators
• Real Estate: Market analysis tools

Focus on solving one specific problem quickly!`,
      tags: ['lead-magnets', 'templates', 'conversion', 'examples']
    },

    // PIF System
    {
      id: 7,
      category: 'pif-system',
      question: 'How does the Pay It Forward (PIF) system work?',
      answer: `The PIF system helps members upgrade through community support:

**How It Works:**
1. Existing members can sponsor new members
2. Sponsored members get upgraded access levels
3. Sponsors earn commissions and build their network
4. Everyone benefits from community growth

**Benefits for New Members:**
• Free upgrade to higher membership tiers
• Access to advanced training materials
• Mentorship from experienced members
• Faster path to success

**Benefits for Sponsors:**
• Commission earnings
• Team building opportunities
• Leadership recognition
• Exclusive sponsor training

**Getting Sponsored:**
Complete your profile, engage in training, and connect with the community. Active, committed members are most likely to receive sponsorship offers.`,
      tags: ['pif', 'sponsorship', 'community', 'upgrades']
    },
    {
      id: 8,
      category: 'pif-system',
      question: 'How can I become a sponsor and help others?',
      answer: `Becoming a sponsor requires:

**Requirements:**
• Rookie Pro level or higher
• Completed Boot Camp training series
• Active community participation
• Demonstrated results with the system

**Sponsorship Process:**
1. Identify promising Boot Camp members
2. Send PIF invitation through the platform
3. Provide mentorship and guidance
4. Earn commissions as they succeed

**Best Practices:**
• Look for engaged, committed learners
• Offer genuine help and support
• Share your success strategies
• Stay connected with sponsored members

**Earnings Potential:**
• Direct commissions on upgrades
• Residual income from team growth
• Bonus rewards for top sponsors
• Recognition in community rankings`,
      tags: ['sponsorship', 'earnings', 'mentorship', 'leadership']
    },

    // Billing & Credits
    {
      id: 9,
      category: 'billing',
      question: 'What are the different membership levels?',
      answer: `BBC Quick List offers progressive membership tiers designed for growth:

**Boot Camp (FREE)**
• Complete Rookie onboarding system
• 3 comprehensive eBooks (List Building, Lead Magnets, Social Conversion)
• Basic lead capture templates
• Rookie Cheat Sheet with tools
• QR Code generator
• Community access
• Achievement & badge system
• All leads captured in your dashboard

**Rookie Pro ($47/month)**
• Everything in Boot Camp, PLUS:
• Advanced training modules & courses
• Premium lead capture templates (insurance, legal, opportunity)
• A/B testing tools
• Priority community support (4hr response)
• Monthly credit bonuses
• 6 months free for Bronze Founders

**Pro ($97/month)**
• Everything in Rookie Pro, PLUS:
• Complete marketing automation
• Advanced analytics & tracking
• Custom branding options
• 1-on-1 coaching sessions (monthly)
• API access for integrations
• 1 year free for Silver Founders

**Enterprise ($197/month)**
• Everything in Pro, PLUS:
• White-label platform options
• Team management & multi-user tools
• Custom integrations & webhooks
• Dedicated account manager
• Private advisory board access
• Lifetime access for Gold Founders

**Upgrade Path:**
Start with free Boot Camp access, upgrade anytime based on your needs. Founder status provides significant discounts and lifetime benefits!`,
      tags: ['membership', 'pricing', 'tiers', 'features']
    },
    {
      id: 10,
      category: 'billing',
      question: 'How do advertising credits work?',
      answer: `Advertising credits help you promote your lead capture pages:

**How Credits Work:**
• Purchase credit packages or earn through achievements
• Use credits for promoted posts on social media
• Track performance and ROI in real-time
• Credits never expire

**Credit Packages:**
• Starter: $50 (includes targeting guidance)
• Growth: $100 (includes A/B testing)
• Scale: $250 (includes advanced analytics)

**Best Practices:**
• Start with smaller campaigns to test
• Use our proven ad templates
• Target specific demographics
• Monitor conversion rates closely

**Pro Tip:** Rookie Pro+ members get monthly credit bonuses and advanced targeting options!`,
      tags: ['credits', 'advertising', 'promotion', 'packages']
    },

    // Technical Support
    {
      id: 11,
      category: 'technical',
      question: 'My lead capture page isn\'t working properly. What should I do?',
      answer: `Common lead capture issues and solutions:

**Page Not Loading:**
• Check your internet connection
• Clear browser cache and cookies
• Try a different browser or device
• Ensure you're using the correct URL

**Form Not Submitting:**
• Verify all required fields are filled
• Check email format is correct
• Disable browser extensions temporarily
• Try on mobile device

**Leads Not Appearing:**
• Allow 5-10 minutes for processing
• Check spam/junk folders for notifications
• Verify your notification email address
• Contact support if issues persist

**Mobile Display Issues:**
• Use mobile-optimized templates
• Test on multiple device types
• Avoid large images or videos
• Keep forms simple and thumb-friendly

**Need Help?** Contact our support team through the Safe List messaging system.`,
      tags: ['troubleshooting', 'technical', 'support', 'issues']
    },
    {
      id: 12,
      category: 'technical',
      question: 'How do I integrate with third-party tools?',
      answer: `BBC Quick List integrates with popular marketing tools:

**Email Marketing Platforms:**
• Mailchimp, ConvertKit, AWeber
• Automatic lead sync
• Tag-based segmentation
• Campaign automation

**CRM Systems:**
• HubSpot, Salesforce, Pipedrive
• Lead scoring and tracking
• Pipeline management
• Activity logging

**Social Media:**
• Facebook/Instagram (lead ads)
• LinkedIn (connection requests)
• Twitter (DM automation)
• TikTok (bio link integration)

**Analytics:**
• Google Analytics integration
• Facebook Pixel tracking
• Conversion tracking
• ROI measurement

**Setup Process:**
1. Go to Settings > Integrations
2. Select your platform
3. Enter API keys/credentials
4. Test connection and sync

**Pro/Enterprise members get priority integration support and custom setups.**`,
      tags: ['integrations', 'third-party', 'automation', 'setup']
    },

    // Account Management
    {
      id: 13,
      category: 'account',
      question: 'How do I update my profile and settings?',
      answer: `Managing your account is easy:

**Profile Updates:**
• Click your profile picture (top right)
• Select "Profile Settings"
• Update name, photo, bio, and contact info
• Add your Tron wallet address for payments

**Privacy Settings:**
• Control who can see your profile
• Manage notification preferences
• Set Safe List messaging permissions
• Configure achievement sharing

**Security Settings:**
• Enable two-factor authentication
• Update password regularly
• Review login activity
• Manage connected devices

**Notification Preferences:**
• Email notifications (leads, messages, achievements)
• SMS alerts (optional with Tron wallet)
• In-app notifications
• Marketing communications

**Important:** Keep your Tron wallet address updated to receive PIF payments and commissions!`,
      tags: ['profile', 'settings', 'security', 'notifications']
    },
    {
      id: 14,
      category: 'billing',
      question: 'How do payments work? Is my money safe?',
      answer: `BBC Quick List uses a **100% non-custodial, peer-to-peer payment system** for complete transparency and security:

**Non-Custodial = You Control Your Money:**
• We do NOT hold your funds
• We do NOT have a wallet in our back office
• We do NOT process your payments
• You receive 100% of your earnings DIRECTLY to YOUR wallet

**How It Works:**
1. **Member-to-Member Direct Payments** - When you earn commissions, payments go directly from the paying member to YOUR personal Tron wallet
2. **Instant Settlement** - Receive USDT (TRC-20) instantly to your wallet address
3. **Full Transparency** - All transactions are recorded on the Tron blockchain
4. **Zero Middleman** - No payment processor delays or holds
5. **You Keep 100%** - No payment processing fees deducted from your earnings

**Why Non-Custodial is Better:**
✅ **Full Control** - Your money, your wallet, your control
✅ **Instant Access** - Funds available immediately
✅ **No Holds** - No 30-day payment cycles or processing delays
✅ **Maximum Security** - We can't access, freeze, or hold your funds
✅ **Blockchain Verified** - Every transaction is transparent and verifiable

**Your Responsibility:**
• Set up your own Tron wallet (Trust Wallet, TronLink, NOW Wallet)
• Keep your wallet address updated in your profile
• Never share your private keys with anyone
• We recommend Trust Wallet or NOW Wallet for beginners

**Important:** This is a peer-to-peer platform. We facilitate connections between members, but all payments flow directly between users. This protects both you and us!`,
      tags: ['payments', 'non-custodial', 'peer-to-peer', 'crypto', 'wallet', 'security', 'earnings']
    },
    {
      id: 15,
      category: 'account',
      question: 'Can I cancel or downgrade my membership?',
      answer: `Yes, you have full control over your membership:

**Cancellation Process:**
• Go to Settings > Billing
• Select "Cancel Membership"
• Choose cancellation date (immediate or end of period)
• Confirm cancellation

**What Happens When You Cancel:**
• Immediate: Lose access to paid features
• End of Period: Access continues until expiration
• Data Retention: Account data saved for 90 days
• Re-activation: Easy to restart anytime

**Downgrading:**
• Available at any billing cycle
• Immediate access level change
• Pro-rated refunds when applicable
• Keep achievements and progress

**Before Canceling:**
• Export your leads and data
• Download training materials
• Consider PIF options for continued access
• Contact support for assistance

**Note:** Boot Camp access (free tier) is always available, so you never lose basic platform access.`,
      tags: ['cancellation', 'downgrade', 'billing', 'retention']
    }
  ];

  const filteredFAQs = faqData.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const getCategoryIcon = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.icon : HelpCircle;
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
          <HelpCircle className="h-10 w-10 text-blue-600" />
          Frequently Asked Questions
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          Everything you need to know about BBC Quick List
        </p>
        
        {/* Search */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search FAQs..."
              className="pl-10 pr-4 py-2 text-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Category Sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Categories</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="space-y-1">
                {categories.map((category) => {
                  const Icon = category.icon;
                  const categoryCount = faqData.filter(faq => 
                    category.id === 'all' || faq.category === category.id
                  ).length;
                  
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors ${
                        selectedCategory === category.id ? 'bg-blue-50 border-r-2 border-blue-600' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-gray-600" />
                        <span className={`font-medium ${
                          selectedCategory === category.id ? 'text-blue-600' : 'text-gray-700'
                        }`}>
                          {category.label}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {categoryCount}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Content */}
        <div className="lg:col-span-3">
          <div className="space-y-4">
            {filteredFAQs.length > 0 ? (
              filteredFAQs.map((faq) => {
                const isExpanded = expandedItems.has(faq.id);
                const CategoryIcon = getCategoryIcon(faq.category);
                
                return (
                  <Card key={faq.id} className="overflow-hidden">
                    <button
                      className="w-full text-left"
                      onClick={() => toggleExpanded(faq.id)}
                    >
                      <CardHeader className="hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <CategoryIcon className="h-5 w-5 text-blue-600" />
                              {faq.question}
                            </CardTitle>
                            <div className="flex flex-wrap gap-2">
                              {faq.tags.slice(0, 3).map((tag, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div className="ml-4 flex-shrink-0">
                            {isExpanded ? (
                              <ChevronDown className="h-5 w-5 text-gray-600" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-gray-600" />
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </button>
                    
                    {isExpanded && (
                      <CardContent className="border-t bg-gray-50">
                        <div className="prose prose-sm max-w-none">
                          <div className="whitespace-pre-line text-gray-700 leading-relaxed">
                            {faq.answer}
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No FAQs Found</h3>
                  <p className="text-gray-600 mb-6">
                    Try adjusting your search terms or selecting a different category.
                  </p>
                  <Button onClick={() => {setSearchTerm(''); setSelectedCategory('all');}}>
                    Clear Filters
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Contact Support */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-8 text-center">
          <MessageSquare className="h-12 w-12 text-blue-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-blue-900 mb-2">Still Need Help?</h3>
          <p className="text-blue-700 mb-6">
            Can't find what you're looking for? Our support team is here to help through the Safe List messaging system.
          </p>
          <Button className="bg-blue-600 hover:bg-blue-700">
            <MessageSquare className="h-4 w-4 mr-2" />
            Contact Support
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default FAQ;